/**
 * Where to send the user after a successful login.
 *
 * `App` redirects an unauthenticated visit to `/login` with
 * `state: { from: "<path the guard caught>" }`, and that state rides along
 * through the login itself since no new navigation replaces it until one
 * actually happens. This is the one place that reads it back, so both the
 * "bounced off a protected route" case and the "typed /login by hand" case
 * (no `from` at all) resolve through the same rule.
 *
 * Pure and DOM-free on purpose: the one thing that matters — never redirect
 * off-site on the strength of history state a page could have set to
 * anything — is exactly what a render test would not force you to prove.
 */
export function resolveLoginRedirect(state: unknown, fallback = "/"): string {
  if (state && typeof state === "object" && "from" in state) {
    const from = (state as { from: unknown }).from;
    // Only ever an in-app path. "//evil.example" parses as protocol-relative
    // in a browser, so a single leading slash is not enough of a check.
    if (typeof from === "string" && from.startsWith("/") && !from.startsWith("//")) {
      return from;
    }
  }
  return fallback;
}

/** Only ever an in-app path — see `resolveLoginRedirect` above for why a
    single leading slash does not clear a value on its own. */
function isInAppPath(path: string): boolean {
  return path.startsWith("/") && !path.startsWith("//");
}

/**
 * The URL "Увійти через Google" navigates the whole browser to (a real
 * `window.location.assign`, never a `fetch` — the point is the 302 chain
 * through Google and back).
 *
 * `returnTo` is meant to be whatever `resolveLoginRedirect` already resolved
 * for this visit, but it rides through the query string to the backend and
 * back, same as `state.from` does through history state — so it gets the
 * same off-site guard here rather than trusting the caller to have applied
 * one already.
 */
export function googleStartUrl(returnTo: string): string {
  const safe = isInAppPath(returnTo) ? returnTo : "/";
  return `/api/auth/google/start?return_to=${encodeURIComponent(safe)}`;
}

/** `/login?error=` codes the Google callback redirects back with on failure. */
export type LoginErrorCode = "no_account" | "google_failed" | "account_disabled";

const LOGIN_ERROR_MESSAGES: Record<LoginErrorCode, string> = {
  no_account:
    "Для цієї Google-пошти немає акаунта в Jarvis. Попроси власника створити його.",
  google_failed: "Не вдалося увійти через Google. Спробуй ще раз.",
  account_disabled: "Акаунт вимкнено.",
};

/** Ukrainian text for a `?error=` code, or `null` for an unknown/missing one. */
export function describeLoginError(code: string | null): string | null {
  if (code !== null && Object.hasOwn(LOGIN_ERROR_MESSAGES, code)) {
    return LOGIN_ERROR_MESSAGES[code as LoginErrorCode];
  }
  return null;
}
